<template>
    <h3 class="my-2 font-medium font-bold text-gray-400 text-lg">
        Histórico da Assinatura
    </h3>
    <div class="flex flex-col dark:text-white">
        <div>
            <div
                class="min-w-0 w-full h-50 rounded-lg overflow-hidden bg-white dark:bg-gray-800 mb-4 flex flex-col drop-shadow-sm p-5"
                v-if="order && order.subscription && loadedHistories.length > 0"
            >
                <ol class="relative border-l border-gray-200 dark:border-gray-700">
                    <li class="mb-10 ml-4" v-for="history in loadedHistories" :key="history.id">
                        <div
                            class="absolute w-3 h-3 rounded-full mt-1.5 -left-1.5 border border-white dark:border-gray-900"
                            :class="getStatusColor(history.status)"
                        ></div>
                        <p class="text-base font-normal text-gray-500 dark:text-gray-200">
                            <strong>{{ formatStatus(history.status) }}</strong>
                        </p>
                        <p
                            v-if="history.reference_date"
                            class="text-sm text-gray-500 dark:text-gray-300"
                        >
                            Mês de Referência: {{ formatDate(history.reference_date) }}
                        </p>

                        <!-- Metadata Display -->
                        <div v-if="history.metadata && history.status === 'canceled'" class="mt-3 text-sm">
                            <!-- 1. Cancelamento via Banco (Webhook) -->
                            <div v-if="history.metadata.origin === 'bank_app' || history.metadata.webhook_source === 'starkbank'"
                                 class="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border-l-4 border-blue-500 space-y-2">
                                <p class="font-semibold text-blue-800 dark:text-blue-200 flex items-center gap-2">
                                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"></path>
                                    </svg>
                                    Cancelamento via Aplicativo do Banco
                                </p>
                                <p class="text-blue-700 dark:text-blue-300">
                                    O cliente cancelou a autorização PIX diretamente no app do banco.
                                </p>
                                <div class="mt-3 space-y-1 text-xs">
                                    <div class="flex justify-between">
                                        <span class="text-blue-600 dark:text-blue-400">Data/Hora:</span>
                                        <span class="font-mono text-blue-800 dark:text-blue-200">{{ history.metadata.cancelled_at || history.created_at }}</span>
                                    </div>
                                    <div v-if="history.metadata.authorization_external_id" class="flex justify-between">
                                        <span class="text-blue-600 dark:text-blue-400">ID Autorização:</span>
                                        <span class="font-mono text-blue-800 dark:text-blue-200">{{ history.metadata.authorization_external_id }}</span>
                                    </div>
                                    <div v-if="history.metadata.cancellation_reason" class="flex justify-between">
                                        <span class="text-blue-600 dark:text-blue-400">Motivo:</span>
                                        <span class="text-blue-800 dark:text-blue-200">{{ history.metadata.cancellation_reason }}</span>
                                    </div>
                                    <div class="flex justify-between">
                                        <span class="text-blue-600 dark:text-blue-400">Origem:</span>
                                        <span class="text-blue-800 dark:text-blue-200">Webhook / {{ history.metadata.webhook_source || 'StarkBank' }}</span>
                                    </div>
                                </div>
                            </div>

                            <!-- 2. Cancelamento via Reembolso -->
                            <div v-else-if="history.metadata.origin === 'refund' || history.metadata.refund_id"
                                 class="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-lg border-l-4 border-yellow-500 space-y-2">
                                <p class="font-semibold text-yellow-800 dark:text-yellow-200 flex items-center gap-2">
                                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 8h6m-5 0a3 3 0 110 6H9l3 3m-3-6h6m6 1a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                                    </svg>
                                    Cancelamento via Reembolso
                                </p>
                                <p class="text-yellow-700 dark:text-yellow-300">
                                    Assinatura cancelada automaticamente devido a reembolso {{ history.metadata.refund_type || 'Total' }}.
                                </p>
                                <div class="mt-3 space-y-1 text-xs">
                                    <div class="flex justify-between">
                                        <span class="text-yellow-600 dark:text-yellow-400">ID Reembolso:</span>
                                        <span class="font-mono text-yellow-800 dark:text-yellow-200">#{{ history.metadata.refund_id }}</span>
                                    </div>
                                    <div v-if="history.metadata.refund_type" class="flex justify-between">
                                        <span class="text-yellow-600 dark:text-yellow-400">Tipo:</span>
                                        <span :class="history.metadata.is_partial_refund ? 'text-orange-600 dark:text-orange-400 font-semibold' : 'text-yellow-800 dark:text-yellow-200'">
                                            {{ history.metadata.refund_type }}
                                        </span>
                                    </div>
                                    <div v-if="history.metadata.refund_amount" class="flex justify-between">
                                        <span class="text-yellow-600 dark:text-yellow-400">Valor Reembolsado:</span>
                                        <span class="font-semibold text-yellow-800 dark:text-yellow-200">{{ formatCurrency(history.metadata.refund_amount) }}</span>
                                    </div>
                                    <div v-if="history.metadata.transaction_amount" class="flex justify-between">
                                        <span class="text-yellow-600 dark:text-yellow-400">Valor da Transação:</span>
                                        <span class="text-yellow-800 dark:text-yellow-200">{{ formatCurrency(history.metadata.transaction_amount) }}</span>
                                    </div>
                                    <div v-if="history.metadata.operator_id" class="flex justify-between">
                                        <span class="text-yellow-600 dark:text-yellow-400">Operador:</span>
                                        <span class="text-yellow-800 dark:text-yellow-200">ID {{ history.metadata.operator_id }}</span>
                                    </div>
                                </div>
                            </div>

                            <!-- 3. Cancelamento via Administrativo Ticto (Smaug) -->
                            <div v-else-if="history.metadata.origin === 'admin'"
                                 class="bg-orange-50 dark:bg-orange-900/20 p-4 rounded-lg border-l-4 border-orange-500 space-y-2">
                                <p class="font-semibold text-orange-800 dark:text-orange-200 flex items-center gap-2">
                                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path>
                                    </svg>
                                    Cancelamento via Administrativo Ticto
                                </p>
                                <p class="text-orange-700 dark:text-orange-300">
                                    {{ history.metadata.is_manual ? 'Cancelado manualmente' : 'Cancelamento automático do sistema' }}
                                </p>
                                <div class="mt-3 space-y-1 text-xs">
                                    <div class="flex justify-between">
                                        <span class="text-orange-600 dark:text-orange-400">Data/Hora:</span>
                                        <span class="font-mono text-orange-800 dark:text-orange-200">{{ history.metadata.cancelled_at || history.created_at }}</span>
                                    </div>
                                    <div v-if="history.metadata.producer_id" class="flex justify-between">
                                        <span class="text-orange-600 dark:text-orange-400">ID Produtor:</span>
                                        <span class="font-mono text-orange-800 dark:text-orange-200">{{ history.metadata.producer_id }}</span>
                                    </div>
                                    <div v-if="history.metadata.operator_id" class="flex justify-between">
                                        <span class="text-orange-600 dark:text-orange-400">ID Operador:</span>
                                        <span class="font-mono text-orange-800 dark:text-orange-200">{{ history.metadata.operator_id }}</span>
                                    </div>
                                    <div class="flex justify-between">
                                        <span class="text-orange-600 dark:text-orange-400">Tipo:</span>
                                        <span :class="history.metadata.is_automatic ? 'text-gray-600 dark:text-gray-400' : 'text-orange-800 dark:text-orange-200 font-semibold'">
                                            {{ history.metadata.is_manual ? 'Manual' : 'Automático' }}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <!-- 4. Cancelamento via Dashboard (Produtor/Thor) -->
                            <div v-else-if="history.metadata.origin === 'producer' || history.metadata.producer_id"
                                 class="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg border-l-4 border-purple-500 space-y-2">
                                <p class="font-semibold text-purple-800 dark:text-purple-200 flex items-center gap-2">
                                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                                    </svg>
                                    Cancelamento via Dashboard (Produtor)
                                </p>
                                <p class="text-purple-700 dark:text-purple-300">
                                    {{ history.metadata.is_manual ? 'Cancelado manualmente pelo produtor' : 'Cancelamento automático do sistema' }}
                                </p>
                                <div class="mt-3 space-y-1 text-xs">
                                    <div class="flex justify-between">
                                        <span class="text-purple-600 dark:text-purple-400">Data/Hora:</span>
                                        <span class="font-mono text-purple-800 dark:text-purple-200">{{ history.metadata.cancelled_at || history.created_at }}</span>
                                    </div>
                                    <div v-if="history.metadata.producer_id" class="flex justify-between">
                                        <span class="text-purple-600 dark:text-purple-400">ID Produtor:</span>
                                        <span class="font-mono text-purple-800 dark:text-purple-200">{{ history.metadata.producer_id }}</span>
                                    </div>
                                    <div v-if="history.metadata.operator_name" class="flex justify-between">
                                        <span class="text-purple-600 dark:text-purple-400">Usuário Responsável:</span>
                                        <span class="text-purple-800 dark:text-purple-200 font-semibold">{{ history.metadata.operator_name }}</span>
                                    </div>
                                    <div v-if="history.metadata.operator_id" class="flex justify-between">
                                        <span class="text-purple-600 dark:text-purple-400">ID Operador:</span>
                                        <span class="font-mono text-purple-800 dark:text-purple-200">{{ history.metadata.operator_id }}</span>
                                    </div>
                                    <div class="flex justify-between">
                                        <span class="text-purple-600 dark:text-purple-400">Tipo:</span>
                                        <span :class="history.metadata.is_automatic ? 'text-gray-600 dark:text-gray-400' : 'text-purple-800 dark:text-purple-200 font-semibold'">
                                            {{ history.metadata.is_manual ? 'Manual' : 'Automático' }}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <!-- 5. Cancelamento via Customer -->
                            <div v-else-if="history.metadata.origin === 'customer' || history.metadata.from_client"
                                 class="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg border-l-4 border-gray-500 space-y-2">
                                <p class="font-semibold text-gray-800 dark:text-gray-200 flex items-center gap-2">
                                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
                                    </svg>
                                    Cancelamento pelo Cliente
                                </p>
                                <p class="text-gray-700 dark:text-gray-300">
                                    O próprio cliente solicitou o cancelamento da assinatura.
                                </p>
                                <div class="mt-3 space-y-1 text-xs">
                                    <div class="flex justify-between">
                                        <span class="text-gray-600 dark:text-gray-400">Origem:</span>
                                        <span class="text-gray-800 dark:text-gray-200">{{ formatSource(history.metadata.source) }}</span>
                                    </div>
                                    <div v-if="history.metadata.canceled_by_user_id" class="flex justify-between">
                                        <span class="text-gray-600 dark:text-gray-400">ID Cliente:</span>
                                        <span class="font-mono text-gray-800 dark:text-gray-200">{{ history.metadata.canceled_by_user_id }}</span>
                                    </div>
                                </div>
                            </div>

                            <!-- 6. Fallback - Cancelamento Genérico -->
                            <div v-else class="bg-gray-50 dark:bg-gray-700 p-3 rounded">
                                <p class="text-gray-700 dark:text-gray-300">
                                    Origem do cancelamento: <strong>{{ formatSource(history.metadata.source) }}</strong>
                                </p>
                                <p v-if="history.metadata.from_client" class="text-gray-600 dark:text-gray-400 mt-1 text-xs">
                                    (Cancelado pelo próprio cliente)
                                </p>
                            </div>
                        </div>

                        <p class="text-base font-normal text-gray-500 dark:text-gray-200 flex flex-wrap mt-2">
                            <ClockIcon class="w-5 mr-1"></ClockIcon>
                            {{ formatDateTime(history.created_at) }}
                        </p>
                    </li>
                </ol>
                <button
                    v-if="hasMore"
                    type="button"
                    class="self-center mt-2 px-4 py-2 text-sm font-medium rounded-md bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 disabled:opacity-50"
                    :disabled="isLoadingMore"
                    @click="loadMore"
                >
                    {{ isLoadingMore ? "Carregando..." : "Carregar mais" }}
                </button>
            </div>
            <div
                v-else-if="order && order.subscription"
                class="min-w-0 w-full h-50 rounded-lg overflow-hidden bg-white dark:bg-gray-800 mb-4 flex drop-shadow-sm p-5"
            >
                <p class="text-gray-500 dark:text-gray-300">Nenhum histórico de assinatura disponível.</p>
            </div>
        </div>
    </div>
</template>

<script>
import HelperMixin from "../../mixins/HelperMixin.vue";
import { ClockIcon } from "@heroicons/vue/24/outline";

export default {
    props: ["order"],
    mixins: [HelperMixin],
    components: {ClockIcon},
    data() {
        return {
            loadedHistories: [...(this.order?.subscription?.histories ?? [])],
            currentPage: 1,
            perPage: 50,
            totalCount: this.order?.subscription?.histories_count ?? 0,
            isLoadingMore: false,
        };
    },
    computed: {
        hasMore() {
            return this.loadedHistories.length < this.totalCount;
        },
    },
    watch: {
        "order.id"() {
            this.loadedHistories = [...(this.order?.subscription?.histories ?? [])];
            this.currentPage = 1;
            this.totalCount = this.order?.subscription?.histories_count ?? 0;
        },
    },
    methods: {
        async loadMore() {
            if (this.isLoadingMore || !this.hasMore) return;
            this.isLoadingMore = true;
            try {
                const nextPage = this.currentPage + 1;
                const response = await axios.get(
                    `/api/order/${this.order.id}/subscription/histories`,
                    { params: { page: nextPage, per_page: this.perPage } }
                );
                this.loadedHistories.push(...(response.data.data ?? []));
                this.currentPage = nextPage;
            } finally {
                this.isLoadingMore = false;
            }
        },
        formatStatus(status) {
            const statusMap = {
                'pending': 'Pendente',
                'active': 'Ativo',
                'payment_pending': 'Pagamento Pendente',
                'payment_approved': 'Pagamento Aprovado',
                'payment_refused': 'Pagamento Recusado',
                'payment_refunded': 'Pagamento Reembolsado',
                'canceled': 'Cancelado',
                'uncanceled': 'Cancelamento Desfeito',
                'reactivated': 'Reativado',
                'paused': 'Pausado',
                'expired': 'Expirado'
            };
            return statusMap[status] || status;
        },
        getStatusColor(status) {
            const colorMap = {
                'pending': 'bg-yellow-500',
                'active': 'bg-green-500',
                'payment_pending': 'bg-yellow-600',
                'payment_approved': 'bg-green-600',
                'payment_refused': 'bg-red-600',
                'payment_refunded': 'bg-orange-500',
                'canceled': 'bg-red-500',
                'uncanceled': 'bg-blue-600',
                'reactivated': 'bg-blue-500',
                'paused': 'bg-gray-500',
                'expired': 'bg-gray-600'
            };
            return colorMap[status] || 'bg-gray-400';
        },
        formatSource(source) {
            const sourceMap = {
                'dashboard': 'Dashboard',
                'admin': 'Admin'
            };
            return sourceMap[source] || source;
        },
        formatDate(date) {
            if (!date) return '-';
            // Se já estiver formatado, retorna
            if (date.includes('/')) return date;
            // Caso contrário, formata
            const d = new Date(date);
            return d.toLocaleDateString('pt-BR');
        },
        formatDateTime(datetime) {
            if (!datetime) return '-';
            // Se já estiver formatado, retorna
            if (datetime.includes('/')) return datetime;
            // Caso contrário, formata
            const d = new Date(datetime);
            return d.toLocaleString('pt-BR');
        },
        formatCurrency(value) {
            if (!value) return 'R$ 0,00';
            const amount = parseFloat(value) / 100;
            return 'R$ ' + amount.toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.');
        },
    },
};
</script>
